import {
	ClaimSolutionEnum,
	ClaimStatusEnum,
	type OrderClaimDetailResponseDto,
	type OrderClaimUpdateDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { useUpdateClaim } from "Order/Modules/Claim/Api/Mutation/updateClaimMutation";
import {
	type ClaimResolveFormModel,
	getClaimResolveSchema,
} from "Order/Modules/Claim/Components/Form/Schema/orderClaimResolveSchema";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import { Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import dayjs from "dayjs";
import type { FC } from "react";
import { useForm } from "react-hook-form";

const claimResource = Resources.base.order.claim;
const selectOptionsResource =
	Resources.base.claim.claimSelectOptions.claimSolution;

type Props = {
	open: boolean;
	setOpen: (open: boolean) => void;
	claim: OrderClaimDetailResponseDto;
	orderId?: number;
};

export const ResolveModal: FC<Props> = ({ open, setOpen, claim, orderId }) => {
	const { t } = useResource();

	const formDefaultValues: ClaimResolveFormModel = {
		finalSolution: ClaimSolutionEnum.original_partner,
		status: ClaimStatusEnum.resolved,
		dateResolved: dayjs().toISOString(),
	};

	const form = useForm<ClaimResolveFormModel>({
		resolver: zodResolver(getClaimResolveSchema()),
		defaultValues: formDefaultValues,
	});

	const { mutate: updateClaim, isPending } = useUpdateClaim(claim.id, orderId);

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = form;

	const handleUpdateClaimSubmit = () => {
		handleSubmit((data: OrderClaimUpdateDto) => updateClaim(data))();
		setOpen(false);
	};

	const handleCancel = () => {
		form.reset(formDefaultValues);
		setOpen(false);
	};

	return (
		<>
			<Dialog open={open} onClose={handleCancel}>
				<DialogTitle>{t(claimResource.finalSolution)}</DialogTitle>
				<DialogContent sx={{ pb: 1 }}>
					<DialogContentText>Chooose the claim solution</DialogContentText>
					<Select
						id="final-solution"
						label={t(claimResource.finalSolution)}
						fullWidth
						options={createSelectOptions(
							ClaimSolutionEnum,
							t,
							selectOptionsResource,
						)}
						variant="outlined"
						name="finalSolution"
						control={control}
						errors={errors}
					/>
					<DatePicker
						name="dateResolved"
						label={t(claimResource.dateResolved)}
						control={control}
						errors={errors}
					/>
				</DialogContent>
				<DialogActions sx={{ justifyContent: "center" }}>
					<Stack flexDirection="row" columnGap={2} p={2}>
						<Button startIcon={<Close />} onClick={handleCancel} color="error">
							{t(claimResource.cancelButton)}
						</Button>
						<Button
							startIcon={<Check />}
							color="success"
							onClick={handleUpdateClaimSubmit}
							loading={isPending}
						>
							{t(claimResource.saveButton)}
						</Button>
					</Stack>
				</DialogActions>
			</Dialog>
		</>
	);
};
